import { Box, Chip, Typography } from '@mui/material'
import _ from 'lodash'
import * as React from 'react'
import { useJump } from '../../store/App/operations'
import { Control, House, Scenario } from '../../types'
import {
	categoryStr,
	isBoolMachineCategory,
	isDenjiMachineCategory,
	isTwayMachineCategory,
	toStepRate,
} from '../../utils'
import TwayModeBadge from '../TwayModeBadge'
import { CategoryIcon } from './MachineIcon'

// bool / denji は ON/OFF、それ以外は開度%
const StateChip = ({ control }: { control: Control }) => {
	const isOnOff =
		isBoolMachineCategory(control.category) ||
		isDenjiMachineCategory(control.category)

	if (isOnOff) {
		const on = Boolean(control.rate)
		return (
			<Chip
				size="small"
				label={on ? 'オン' : 'オフ'}
				color={on ? 'success' : 'default'}
				variant={on ? 'filled' : 'outlined'}
				sx={{ flexShrink: 0, height: 22 }}
			/>
		)
	}
	const rate = control.rate
	const label = rate <= 0 ? '全閉' : rate >= 100 ? '全開' : `${rate}%開`
	return (
		<Chip
			size="small"
			label={label}
			variant="outlined"
			sx={{ flexShrink: 0, height: 22 }}
		/>
	)
}

function ControlTableCell(props: {
	house: House
	control: Control
	scenario?: Scenario
}) {
	const { house, control, scenario } = props
	const stepRate = toStepRate(control.rate || 0)
	const { jumpMachinePage } = useJump()
	const clickable = Boolean(scenario)

	return (
		<Box
			onClick={() => {
				if (!scenario) return
				jumpMachinePage(house.id, scenario.id, control.id)
			}}
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1.25,
				p: 1,
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 2,
				cursor: clickable ? 'pointer' : 'default',
				transition: 'background-color .12s',
				...(clickable ? { '&:hover': { bgcolor: 'action.hover' } } : {}),
			}}
		>
			<CategoryIcon
				category={control.category}
				stepRate={stepRate}
				style={{ width: 26, height: 26, flexShrink: 0 }}
			/>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography
					sx={{
						fontWeight: 600,
						fontSize: 14,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{control.label}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{categoryStr[control.category]}
				</Typography>
			</Box>
			{isTwayMachineCategory(control.category) && (
				<TwayModeBadge control={control} />
			)}
			<StateChip control={control} />
		</Box>
	)
}

const categoryList = Object.entries(categoryStr).map(([key, title]) => ({
	key,
	title,
}))

type Props = {
	house: House
	scenario?: Scenario
	controls: Control[]
}
function ControlTable({ house, scenario, controls }: Props) {
	const controlsByCategory = _.groupBy(controls, (c) => c.category || 'none')

	return (
		<Box sx={{ display: 'grid', gap: 1.5, px: { xs: 1.5, sm: 0 } }}>
			{categoryList
				.filter((c) => c.key !== 'hp' && controlsByCategory[c.key])
				.map((category) => (
					<Box key={category.key}>
						<Typography
							variant="subtitle2"
							color="text.secondary"
							sx={{ ml: 0.5, mb: 0.75, fontWeight: 700 }}
						>
							{category.title}
						</Typography>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: {
									xs: '1fr',
									sm: 'repeat(auto-fill, minmax(200px, 1fr))',
								},
								gap: 1,
							}}
						>
							{controlsByCategory[category.key]?.map(
								(control: Control, key: number) => (
									<ControlTableCell
										key={key}
										house={house}
										scenario={scenario}
										control={control}
									/>
								),
							)}
						</Box>
					</Box>
				))}
		</Box>
	)
}

export default ControlTable
